import { useCallback, useMemo, useState } from 'react'
import { FetchState } from 'shared/api'
import { flatten, maxText } from 'shared/util'
import { useKeyBind } from 'shared/keyBind'

import { ArticleResult, useGetArticle } from 'interface/article'
import { SisetuResult } from 'interface/sisetu'
import { KojinResult } from 'interface/kojin'

import { useSearch, useTargetList } from 'features/search/context'
import { useKojinPreviewDialog } from 'features/kojin/dialog/preview/hooks'
import { useSisetuPreviewDialog } from 'features/sisetu/dialog/preview/hooks'

type useArticlePreviewProps = {
  articleList?: ArticleResult[]
  articleId?: number | null
	article?: ArticleResult | null
  targetSisetuCode?: string
  targetKojinCode?: string
  targetAuthorId?: number
	onSisetuOpenReply?: (sisetuCode: string | null) => void
  onKojinOpenReply?: (kojinCode: string | null) => void
  onAuthorOpenReply?: (authorId: number | null) => void
  onClose?: () => void
}
export const useArticlePreview = ({
	articleList,
	articleId: defaultArticleId,
	article: defaultArticle,
  targetSisetuCode,
  targetKojinCode,
  targetAuthorId,
	onSisetuOpenReply,
  onKojinOpenReply,
  onAuthorOpenReply,
  onClose
}: useArticlePreviewProps) => {
	const search = useSearch()
	const targetList = useTargetList()

	const [articleId, setArticleId] = useState<number | null>(defaultArticleId ?? defaultArticle?.articleId ?? null)

	const {
		current,
		prev,
		next,
	} = useMemo(
		() => {
			const index = (
				articleId ? (articleList?.findIndex(article => article.articleId === articleId) ?? -1) :
				-1
			)
			return {
				current: (articleList && index >= 0) ? articleList[index] : null,
				next: (articleList && index >= 0 && index < (articleList.length - 1)) ? articleList[index + 1] : null,
				prev: (articleList && index > 0) ? articleList[index - 1] : null
			}
		},
		[articleList, articleId]
	)

	const articleState: FetchState<ArticleResult> = useGetArticle(
		search?.searchId,
		articleId,
		{
			defaultArticle: current ?? defaultArticle ?? undefined,
			updateTime: maxText(targetList?.updateTime, search?.updateTime) ?? undefined
		}
	)
  const article = useMemo(() => articleState.data, [articleState])

	const {
		setSisetuList, sisetuList,
		openSisetu, sisetuCode: selectedSisetuCode
	} = useSisetuPreviewDialog()
	const {
		setKojinList, kojinList,
		openKojin, kojinCode: selectedKojinCode,
		openAuthor, authorId: selectedAuthorId
	} = useKojinPreviewDialog()

	const closeModal = useCallback(
		(exclusive: 'kojin' | 'sisetu' | null = null) => {
			if (exclusive !== 'sisetu') {
				if (onSisetuOpenReply) {
					onSisetuOpenReply(null)
				} else {
					openSisetu(null)
				}
			}
			if (exclusive !== 'kojin') {
				if (onKojinOpenReply) {
					onKojinOpenReply(null)
				} else {
					openKojin(null)
				}
			}
		},
		[onSisetuOpenReply, openSisetu, onKojinOpenReply, openKojin]
	)
	const onSisetuOpen = useCallback(
		(otherSisetuCode: string | null, list?: SisetuResult[]) => {
			closeModal('sisetu')
			if (onSisetuOpenReply) {
				onSisetuOpenReply(otherSisetuCode)
			} else {
				setSisetuList(list ?? [])
				openSisetu(otherSisetuCode)
			}
		},
		[closeModal, onSisetuOpenReply, openSisetu, setSisetuList]
	)
	const onKojinOpen = useCallback(
		(otherKojinCode: string | null, list?: KojinResult[]) => {
			closeModal('kojin')
			if (onKojinOpenReply) {
				onKojinOpenReply(otherKojinCode)
			} else {
				setKojinList(list ?? [])
				openKojin(otherKojinCode)
			}
		},
		[closeModal, onKojinOpenReply, openKojin, setKojinList]
	)
	const onAuthorOpen = useCallback(
		(otherAuthorId: number | null) => {
			closeModal('kojin')
			if (onAuthorOpenReply) {
				onAuthorOpenReply(otherAuthorId)
			} else {
				openAuthor(otherAuthorId)
			}
		},
		[closeModal, onAuthorOpenReply, openAuthor]
	)

	const isShowModal = useMemo(
		() => !!(selectedKojinCode || selectedAuthorId),
		[selectedKojinCode, selectedAuthorId]
	)
	
	const openPrev = useCallback(
		() => {
			if (isShowModal) return
			if (prev?.articleId) setArticleId(prev.articleId)
		},
		[prev, setArticleId, isShowModal]
	)
	const openNext = useCallback(
		() => {
			if (isShowModal) return
			if (next?.articleId) setArticleId(next.articleId)
		},
		[next, setArticleId, isShowModal]
	)

  useKeyBind({ key: "PageUp", onKeyDown: openPrev })
  useKeyBind({ key: "PageDown", onKeyDown: openNext })

	return {
		loading: articleState.loading,
		error: flatten([articleState.error]),
		articleList, articleId, article, 
		hasPrev: prev !== null ? true : false,
		hasNext: next !== null ? true : false,
		openPrev, openNext,
		targetSisetuCode,
		targetKojinCode,
		targetAuthorId,
		onSisetuOpen, selectedSisetuCode, sisetuList,
		onKojinOpen, selectedKojinCode, kojinList,
		onAuthorOpen, selectedAuthorId,
		closeModal,
		onClose
	}
}
